import React, { useState, useEffect, useRef } from 'react';
import { UserProfile, VoiceConfig } from '../types';
import { generateMusicPlaylist, getARInstructions } from '../services/geminiService';
import { Mic, Music, Camera, Video, Play, Pause, Volume2, Settings2, Sparkles, ChefHat, Dumbbell, User, Loader2, X, Edit2 } from 'lucide-react';

interface Props {
  user: UserProfile;
  onUpdateUser: (user: UserProfile) => void;
}

const StudioHub: React.FC<Props> = ({ user, onUpdateUser }) => {
  const [activeTab, setActiveTab] = useState<'sound' | 'visual'>('sound');
  
  // Sound State
  const [isPlayingMusic, setIsPlayingMusic] = useState(false);
  const [playlist, setPlaylist] = useState<{tracks: string[], description: string} | null>(null);
  const [loadingMusic, setLoadingMusic] = useState(false);
  const [voicePreview, setVoicePreview] = useState(false);

  // Visual State
  const [arMode, setArMode] = useState<'cooking' | 'sports' | null>(null);
  const [arInstruction, setArInstruction] = useState('');
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [showAvatarEdit, setShowAvatarEdit] = useState(false);

  const voices = [
      { id: 'fa-male-1', label: 'آرش', gender: 'male' },
      { id: 'fa-female-1', label: 'سارا', gender: 'female' },
      { id: 'fa-male-2', label: 'نیما', gender: 'male' },
      { id: 'fa-female-2', label: 'مریم', gender: 'female' },
      { id: 'fa-male-3', label: 'کاوه', gender: 'male' },
      { id: 'fa-female-3', label: 'رویا', gender: 'female' },
      { id: 'fa-male-4', label: 'سامان', gender: 'male' },
      { id: 'fa-female-4', label: 'پریا', gender: 'female' },
  ];

  useEffect(() => {
    // Stop camera when leaving Visual tab or AR mode
    if (activeTab !== 'visual' || !arMode) {
        stopCamera();
    }
  }, [activeTab, arMode]);

  const startCamera = async () => {
      try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          if (videoRef.current) {
              videoRef.current.srcObject = stream;
              setCameraActive(true);
          }
      } catch (err) {
          console.error("Camera error:", err);
          alert("دسترسی به دوربین امکان‌پذیر نیست.");
      }
  };

  const stopCamera = () => {
      if (videoRef.current && videoRef.current.srcObject) {
          const stream = videoRef.current.srcObject as MediaStream;
          stream.getTracks().forEach(track => track.stop());
          videoRef.current.srcObject = null;
          setCameraActive(false);
      }
  };

  const handleVoiceChange = (field: keyof VoiceConfig, value: any) => {
      onUpdateUser({
          ...user,
          immersiveProfile: {
              ...user.immersiveProfile,
              voice: {
                  ...user.immersiveProfile.voice,
                  [field]: value
              }
          }
      });
  };

  const handleTestVoice = () => {
      setVoicePreview(true);
      if ('speechSynthesis' in window) {
          const utterance = new SpeechSynthesisUtterance("سلام! من صدای همراه شما هستم. امیدوارم روز خوبی داشته باشید.");
          utterance.lang = 'fa-IR';
          utterance.rate = user.immersiveProfile.voice.speed;
          utterance.pitch = user.immersiveProfile.voice.pitch;
          
          utterance.onend = () => setVoicePreview(false);
          window.speechSynthesis.speak(utterance);
      } else {
          setTimeout(() => setVoicePreview(false), 2000);
          alert('مرورگر شما از پخش صدا پشتیبانی نمی‌کند.');
      }
  };

  const handleGenerateMusic = async (mood: 'focus' | 'relax' | 'energy') => {
      setLoadingMusic(true);
      try {
          const res = await generateMusicPlaylist(user, mood);
          setPlaylist(res);
      } catch (e) {
          console.error(e);
      } finally {
          setLoadingMusic(false);
      }
  };

  const handleStartAR = async (mode: 'cooking' | 'sports') => {
      setArMode(mode);
      const instruction = await getARInstructions(user, mode);
      setArInstruction(instruction);
      startCamera();
  };

  const handleStopAR = () => {
      setArMode(null);
      stopCamera();
  };

  return (
    <div className="h-full flex flex-col animate-fade-in pb-20">
       <div className="flex items-center justify-between mb-6">
        <div>
           <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
             <Video className="w-6 h-6 text-rose-500" />
             استودیو خلاق
           </h2>
           <p className="text-slate-500 text-sm mt-1">شخصی‌سازی صدا، موسیقی و تجربه واقعیت افزوده</p>
        </div>
        <div className="flex bg-white rounded-xl border border-slate-200 p-1">
            <button 
                onClick={() => setActiveTab('sound')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'sound' ? 'bg-slate-100 text-slate-800' : 'text-slate-500'}`}>
                صدا و موسیقی
            </button>
            <button 
                onClick={() => setActiveTab('visual')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'visual' ? 'bg-slate-100 text-slate-800' : 'text-slate-500'}`}>
                آزمایشگاه تصویری
            </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
          {activeTab === 'sound' && (
              <div className="space-y-6">
                  {/* Voice Settings */}
                  <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
                      <div className="flex items-center gap-3 mb-6">
                          <div className="p-3 bg-blue-50 text-blue-500 rounded-xl">
                              <Mic className="w-6 h-6" />
                          </div>
                          <h3 className="font-bold text-lg text-slate-800">تنظیمات صدای همراه</h3>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                          {voices.map(v => (
                              <button
                                key={v.id}
                                onClick={() => handleVoiceChange('id', v.id)}
                                className={`p-3 rounded-xl border flex flex-col items-center gap-2 transition-all ${user.immersiveProfile.voice.id === v.id ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100'}`}
                              >
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${user.immersiveProfile.voice.id === v.id ? 'bg-white/20' : 'bg-white'}`}>
                                      {v.gender === 'male' ? '👨' : '👩'}
                                  </div>
                                  <span className="text-sm font-medium">{v.label}</span>
                              </button>
                          ))}
                      </div>

                      <div className="space-y-4 mb-6">
                          <div>
                              <div className="flex justify-between text-sm text-slate-500 mb-1">
                                  <span>سرعت گفتار</span>
                                  <span>{user.immersiveProfile.voice.speed}x</span>
                              </div>
                              <input 
                                type="range" min="0.5" max="2" step="0.1"
                                value={user.immersiveProfile.voice.speed}
                                onChange={(e) => handleVoiceChange('speed', parseFloat(e.target.value))}
                                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                              />
                          </div>
                          <div>
                              <div className="flex justify-between text-sm text-slate-500 mb-1">
                                  <span>تن صدا (Pitch)</span>
                                  <span>{user.immersiveProfile.voice.pitch}</span>
                              </div>
                              <input 
                                type="range" min="0.5" max="2" step="0.1"
                                value={user.immersiveProfile.voice.pitch}
                                onChange={(e) => handleVoiceChange('pitch', parseFloat(e.target.value))}
                                className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                              />
                          </div>
                      </div>

                      <button 
                        onClick={handleTestVoice}
                        disabled={voicePreview}
                        className="w-full py-3 border border-blue-100 bg-blue-50 text-blue-600 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-blue-100 transition-colors"
                      >
                          {voicePreview ? <Loader2 className="animate-spin w-5 h-5" /> : <Play className="w-5 h-5 fill-current" />}
                          {voicePreview ? 'در حال پخش...' : 'تست صدای انتخاب شده'}
                      </button>
                  </div>

                  {/* Music Generation */}
                  <div className="bg-gradient-to-br from-indigo-900 to-purple-900 p-6 rounded-3xl shadow-lg text-white">
                       <div className="flex items-center gap-3 mb-6">
                          <div className="p-3 bg-white/10 rounded-xl">
                              <Music className="w-6 h-6" />
                          </div>
                          <h3 className="font-bold text-lg">موسیقی زمینه هوشمند</h3>
                      </div>
                      
                      <p className="text-indigo-200 text-sm mb-6">
                          موسیقی متناسب با حال و هوای خود را تولید کنید. هوش مصنوعی بر اساس سلیقه شما ({user.lifeProfile.mental.interests[0] || 'عمومی'}) انتخاب می‌کند.
                      </p>

                      <div className="grid grid-cols-3 gap-3 mb-6">
                          <button onClick={() => handleGenerateMusic('focus')} className="p-3 bg-white/10 hover:bg-white/20 rounded-xl flex flex-col items-center gap-2 transition-colors">
                              <span className="text-2xl">🧠</span>
                              <span className="text-xs font-bold">تمرکز</span>
                          </button>
                          <button onClick={() => handleGenerateMusic('relax')} className="p-3 bg-white/10 hover:bg-white/20 rounded-xl flex flex-col items-center gap-2 transition-colors">
                              <span className="text-2xl">🌿</span>
                              <span className="text-xs font-bold">آرامش</span>
                          </button>
                          <button onClick={() => handleGenerateMusic('energy')} className="p-3 bg-white/10 hover:bg-white/20 rounded-xl flex flex-col items-center gap-2 transition-colors">
                              <span className="text-2xl">⚡</span>
                              <span className="text-xs font-bold">انرژی</span>
                          </button>
                      </div>

                      {playlist && (
                          <div className="bg-black/30 rounded-xl p-4 animate-fade-in">
                              <div className="flex items-center justify-between mb-2">
                                  <h4 className="font-bold text-sm">لیست پخش پیشنهادی</h4>
                                  <Volume2 className="w-4 h-4 text-emerald-400" />
                              </div>
                              <p className="text-xs text-indigo-300 mb-3 italic">{playlist.description}</p>
                              <div className="space-y-2">
                                  {playlist.tracks.map((track, i) => (
                                      <div key={i} className="flex items-center gap-2 text-sm bg-white/5 p-2 rounded-lg">
                                          <span className="text-indigo-400 font-mono text-xs">{i+1}</span>
                                          <span>{track}</span>
                                      </div>
                                  ))}
                              </div>
                              <div className="mt-4 flex gap-2">
                                  <button 
                                    onClick={() => setIsPlayingMusic(!isPlayingMusic)}
                                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white py-2 rounded-lg font-bold text-sm transition-colors flex items-center justify-center gap-2"
                                  >
                                      {isPlayingMusic ? <Pause className="w-4 h-4 fill-current" /> : <Play className="w-4 h-4 fill-current" />}
                                      {isPlayingMusic ? 'توقف' : 'پخش لیست'}
                                  </button>
                              </div>
                          </div>
                      )}

                      {loadingMusic && (
                           <div className="flex justify-center py-4">
                               <Loader2 className="w-8 h-8 animate-spin text-white/50" />
                           </div>
                      )}
                  </div>
              </div>
          )}

          {activeTab === 'visual' && (
              <div className="space-y-6">
                  {/* AR Viewport */}
                  {arMode ? (
                      <div className="relative bg-black rounded-3xl overflow-hidden aspect-[3/4] shadow-xl animate-fade-in">
                          <video 
                            ref={videoRef} 
                            autoPlay 
                            playsInline 
                            className="w-full h-full object-cover"
                          />
                          
                          {/* AR Overlay */}
                          <div className="absolute inset-0 pointer-events-none flex flex-col justify-between p-6">
                              <div className="bg-black/60 backdrop-blur-md text-white p-4 rounded-2xl border border-white/10">
                                  <div className="flex items-center gap-2 mb-2 text-emerald-400 font-bold">
                                      <Sparkles className="w-4 h-4" />
                                      {arMode === 'cooking' ? 'دستیار آشپزی AR' : 'مربی ورزش AR'}
                                  </div>
                                  <p className="text-sm leading-relaxed">{arInstruction}</p>
                              </div>

                              <div className="flex justify-center">
                                  {arMode === 'cooking' && (
                                      <div className="animate-bounce bg-white/80 text-slate-900 px-4 py-2 rounded-full text-sm font-bold shadow-lg">
                                          🥕 مواد را اینجا قرار دهید
                                      </div>
                                  )}
                                  {arMode === 'sports' && (
                                      <div className="border-4 border-dashed border-emerald-500 w-48 h-64 rounded-3xl opacity-50"></div>
                                  )}
                              </div>

                              <div className="pointer-events-auto flex justify-center">
                                  <button 
                                    onClick={handleStopAR}
                                    className="bg-red-600 text-white px-8 py-3 rounded-full font-bold hover:bg-red-700 shadow-lg transition-transform hover:scale-105"
                                  >
                                      خروج از حالت AR
                                  </button>
                              </div>
                          </div>
                      </div>
                  ) : (
                      <div className="space-y-6">
                          {/* 3D Avatar Preview (Static for now) */}
                          <div className="bg-slate-900 rounded-3xl p-8 text-center relative overflow-hidden group">
                              <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80 z-10"></div>
                              <img 
                                src="https://img.freepik.com/free-psd/3d-illustration-person-with-sunglasses_23-2149436188.jpg" 
                                alt="3D Avatar" 
                                className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-700"
                              />
                              
                              <div className="relative z-20 flex flex-col items-center h-full justify-end pb-4">
                                  <div className="w-24 h-24 rounded-full border-4 border-white shadow-xl overflow-hidden mb-4">
                                      <img src="https://img.freepik.com/free-psd/3d-illustration-person-with-sunglasses_23-2149436188.jpg" className="w-full h-full object-cover" />
                                  </div>
                                  <h3 className="text-white font-bold text-xl mb-1">آواتار شما</h3>
                                  <p className="text-slate-300 text-sm mb-6">نمایش سه‌بعدی در محیط واقعیت افزوده</p>
                                  <button 
                                    onClick={() => setShowAvatarEdit(true)}
                                    className="bg-white text-slate-900 px-6 py-2 rounded-full text-sm font-bold hover:bg-slate-100 transition-colors"
                                  >
                                      ویرایش ظاهر
                                  </button>
                              </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                              <button 
                                onClick={() => handleStartAR('cooking')}
                                className="bg-orange-50 p-6 rounded-3xl border border-orange-100 hover:shadow-lg hover:border-orange-200 transition-all text-center group"
                              >
                                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm group-hover:scale-110 transition-transform">
                                      <ChefHat className="w-8 h-8 text-orange-500" />
                                  </div>
                                  <h4 className="font-bold text-slate-800 mb-1">آموزش آشپزی AR</h4>
                                  <p className="text-xs text-slate-500">نمایش مواد و مراحل روی میز</p>
                              </button>

                              <button 
                                onClick={() => handleStartAR('sports')}
                                className="bg-emerald-50 p-6 rounded-3xl border border-emerald-100 hover:shadow-lg hover:border-emerald-200 transition-all text-center group"
                              >
                                  <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm group-hover:scale-110 transition-transform">
                                      <Dumbbell className="w-8 h-8 text-emerald-500" />
                                  </div>
                                  <h4 className="font-bold text-slate-800 mb-1">مربی ورزش AR</h4>
                                  <p className="text-xs text-slate-500">تصحیح حرکات با دوربین</p>
                              </button>
                          </div>
                      </div>
                  )}
              </div>
          )}

          {/* Simple Avatar Edit Modal */}
          {showAvatarEdit && (
              <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6 animate-fade-in" onClick={() => setShowAvatarEdit(false)}>
                  <div className="bg-white rounded-3xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
                      <div className="flex justify-between items-center mb-4">
                          <h3 className="font-bold text-lg">ویرایش آواتار</h3>
                          <button onClick={() => setShowAvatarEdit(false)}><X className="w-5 h-5 text-slate-400" /></button>
                      </div>
                      <p className="text-sm text-slate-500 mb-6">این قابلیت در نسخه دمو محدود است. به زودی می‌توانید لباس و چهره را تغییر دهید.</p>
                      <button onClick={() => setShowAvatarEdit(false)} className="w-full bg-slate-900 text-white py-3 rounded-xl font-bold">بستن</button>
                  </div>
              </div>
          )}
      </div>
    </div>
  );
};

export default StudioHub;